import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  TREASURY_FORM_STATUS_OPTIONS,
  TREASURY_FORM_TYPE_OPTIONS,
} from "../constants/treasuryFormsOptions";
import {
  getFormStatusFilterLabel,
  getFormTypeLabel,
} from "../utils/treasuryFormsFormatters";

const filterControlSx = {
  height: 34,
  bgcolor: "white",
  borderRadius: "10px",
  color: "primary.900",
  fontSize: 13,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(20, 32, 82, 0.18)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "primary.400",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "primary.600",
  },
  "& .MuiSelect-select": {
    py: 0.65,
    fontSize: 13,
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSvgIcon-root": { fontSize: 18 },
};

const menuProps = {
  PaperProps: {
    sx: {
      "& .MuiMenuItem-root": { minHeight: 34, fontSize: 13 },
    },
  },
};

function FilterSelect({
  value,
  onValueChange,
  placeholder,
  getLabel,
  ariaLabel,
  onClear,
  clearAriaLabel,
  minWidth,
  children,
}) {
  return (
    <Select
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      displayEmpty
      size="small"
      MenuProps={menuProps}
      inputProps={{ "aria-label": ariaLabel || placeholder }}
      IconComponent={value && onClear ? () => null : undefined}
      sx={{
        minWidth: { xs: "100%", sm: minWidth },
        ...filterControlSx,
        "& .MuiSelect-select": {
          ...filterControlSx["& .MuiSelect-select"],
          pr: value && onClear ? "31px !important" : undefined,
        },
      }}
      renderValue={(selectedValue) =>
        selectedValue ? getLabel?.(selectedValue) || selectedValue : placeholder
      }
      endAdornment={
        value && onClear ? (
          <InputAdornment
            position="end"
            sx={{ position: "absolute", right: 6, color: "primary.900" }}
          >
            <IconButton
              size="small"
              aria-label={clearAriaLabel || `Limpiar filtro ${placeholder.toLowerCase()}`}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
                onClear();
              }}
              sx={{ width: 23, height: 23, opacity: 0.65, "&:hover": { opacity: 1 } }}
            >
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </InputAdornment>
        ) : null
      }
    >
      {children}
    </Select>
  );
}

export default function PlantillasFilters({
  nameInput,
  onNameInputChange,
  onClearName,
  typeFilter,
  onTypeChange,
  statusFilter,
  onStatusChange,
}) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent="flex-end"
      sx={{ flexWrap: { sm: "wrap", xl: "nowrap" } }}
    >
      <TextField
        value={nameInput}
        onChange={(event) => onNameInputChange(event.target.value)}
        placeholder="Buscar por nombre"
        size="small"
        slotProps={{ htmlInput: { maxLength: 255 } }}
        aria-label="Buscar plantilla por nombre"
        sx={{
          width: { xs: "100%", sm: 205 },
          "& .MuiOutlinedInput-root": {
            height: 34,
            borderRadius: "10px",
            bgcolor: "white",
            color: "primary.900",
            fontSize: 13,
            "& fieldset": { borderColor: "rgba(20, 32, 82, 0.18)" },
            "&:hover fieldset": { borderColor: "primary.400" },
            "&.Mui-focused fieldset": { borderColor: "primary.600" },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "primary.800", fontSize: 17 }} />
            </InputAdornment>
          ),
          endAdornment: nameInput ? (
            <InputAdornment position="end">
              <Tooltip title="Limpiar búsqueda">
                <IconButton
                  size="small"
                  aria-label="Limpiar búsqueda"
                  onClick={onClearName}
                  sx={{ width: 23, height: 23, color: "primary.900", opacity: 0.65 }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ) : null,
        }}
      />

      <FilterSelect
        value={typeFilter}
        onValueChange={onTypeChange}
        onClear={() => onTypeChange("")}
        placeholder="Tipo"
        ariaLabel="Filtrar por tipo"
        clearAriaLabel="Limpiar filtro de tipo"
        getLabel={getFormTypeLabel}
        minWidth={138}
      >
        <MenuItem value="">Todos los tipos</MenuItem>
        {TREASURY_FORM_TYPE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </FilterSelect>

      <FilterSelect
        value={statusFilter}
        onValueChange={onStatusChange}
        onClear={() => onStatusChange("")}
        placeholder="Estado"
        ariaLabel="Filtrar por estado"
        clearAriaLabel="Limpiar filtro de estado"
        getLabel={getFormStatusFilterLabel}
        minWidth={126}
      >
        <MenuItem value="">Todos los estados</MenuItem>
        {TREASURY_FORM_STATUS_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </FilterSelect>
    </Stack>
  );
}
